import { useEffect, useRef, useState } from 'react';
import { api, type AudioTrack } from '../../api/client';
import { Button, Card, EmptyState, IconButton, Spinner } from '../ui';
import { useUiStore } from '../../store/uiStore';
import styles from './MusicManager.module.css';

const VOL_KEY = 'gmbinder:vol';

export interface MusicManagerProps {
  slug: string;
}

/**
 * Full-section background-music manager: upload (drag & drop), list, preview-play,
 * loop and delete tracks. Shared by the editor "Musica" section and the binder runtime.
 */
export function MusicManager({ slug }: MusicManagerProps) {
  const toast = useUiStore((s) => s.toast);
  const [tracks, setTracks] = useState<AudioTrack[]>([]);
  const [loading, setLoading] = useState(true);
  const [current, setCurrent] = useState<AudioTrack | null>(null);
  const [loop, setLoop] = useState(true);
  const [dragging, setDragging] = useState(false);
  const audioRef = useRef<HTMLAudioElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const refresh = async () => {
    try {
      const { tracks: t } = await api.listAudio(slug);
      setTracks(t);
    } catch {
      /* ignore — offline or empty */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    void refresh();
    const saved = Number(localStorage.getItem(VOL_KEY));
    if (audioRef.current && !Number.isNaN(saved) && saved > 0) audioRef.current.volume = saved;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const play = (track: AudioTrack) => {
    setCurrent(track);
    setLoop(track.loop ?? true);
    requestAnimationFrame(() => {
      if (audioRef.current) {
        audioRef.current.src = track.url;
        void audioRef.current.play().catch(() => undefined);
      }
    });
  };

  const upload = async (files: FileList | null) => {
    if (!files?.length) return;
    try {
      const { tracks: t } = await api.uploadAudio(slug, Array.from(files));
      setTracks(t);
      toast('Brani caricati', 'success');
    } catch {
      toast('Upload fallito', 'danger');
    }
  };

  const remove = async (file: string) => {
    try {
      const { tracks: t } = await api.deleteAudio(slug, file);
      setTracks(t);
      if (current?.file === file) setCurrent(null);
      toast('Brano eliminato', 'info');
    } catch {
      toast('Eliminazione fallita', 'danger');
    }
  };

  return (
    <div className={styles.wrap}>
      <Card className={styles.player}>
        <div className={styles.nowTitle}>{current?.title ?? 'Nessuna traccia in riproduzione'}</div>
        {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
        <audio
          ref={audioRef}
          className={styles.audio}
          controls
          loop={loop}
          preload="none"
          onEnded={() => {
            if (loop || !current) return;
            const idx = tracks.findIndex((t) => t.file === current.file);
            const next = tracks[idx + 1];
            if (next) play(next);
          }}
          onVolumeChange={(e) =>
            localStorage.setItem(VOL_KEY, String((e.target as HTMLAudioElement).volume))
          }
        />
        <label className={styles.loop}>
          <input type="checkbox" checked={loop} onChange={(e) => setLoop(e.target.checked)} /> 🔁 Loop
        </label>
      </Card>

      <div
        className={[styles.dropzone, dragging ? styles.dragging : undefined].filter(Boolean).join(' ')}
        onClick={() => fileRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          void upload(e.dataTransfer.files);
        }}
      >
        ⬆️ Trascina i brani qui o clicca per scegliere
        <input
          ref={fileRef}
          type="file"
          accept="audio/*,.mp3,.ogg,.wav,.m4a,.opus,.flac,.aac"
          multiple
          hidden
          onChange={(e) => void upload(e.target.files)}
        />
      </div>

      {loading ? (
        <div className={styles.center}>
          <Spinner />
        </div>
      ) : tracks.length === 0 ? (
        <EmptyState icon="🎵" title="Nessun brano" description="Carica i tuoi file audio qui sopra." />
      ) : (
        <ul className={styles.list}>
          {tracks.map((t) => {
            const active = current?.file === t.file;
            return (
              <li
                key={t.file}
                className={[styles.track, active ? styles.trackActive : undefined]
                  .filter(Boolean)
                  .join(' ')}
              >
                <Button variant="ghost" size="sm" onClick={() => play(t)}>
                  {active ? '♪' : '▸'}
                </Button>
                <span className={styles.trackName}>{t.title}</span>
                <IconButton label={`Elimina ${t.title}`} size="sm" onClick={() => void remove(t.file)}>
                  ✕
                </IconButton>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
